import { compatibleDate } from '~/libs/date-helpers'
import { normalizeLoanType } from '~/libs/loan-product-info'
import { getApprovalOption, getApprovalMaxAmount } from '~/libs/approval-helper'

const TYPE_ORDER = ['sba', 'term', 'loc', 'ach'].reverse()

function getOfferPayback(offer) {
  if (!offer) {
    return null;
  }
  if (Number(offer.payback) > 0) {
    return Number(offer.payback)
  }
  if (!(Number(offer.factorRate) > 0) || !(Number(offer.amount) > 0)) {
    return null;
  }
  const factorRate = Number(offer.factorRate)
  // calculate payback only for a valid factor rate
  if (factorRate < 1 || factorRate >= 2) {
    return null;
  }
  return offer.amount * factorRate
}

function getIsPrioritized(option) {
  return option?.isPrioritized || false
}

function getIsRecommended(option) {
  return option?.isRecommended || false
}

function getIsVirtual(option) {
  return option?.isVirtual || false
}

export function sortOptions (output, initialChange, a, b) {
  const aOption = !a.isStaticOffer ? getApprovalOption(a, initialChange) : {};
  const bOption = !b.isStaticOffer ? getApprovalOption(b, initialChange) : {};

  // TODO: adjust this to account for actual options that are returned as a result of the virtual product
  // Ideally the approvals/offers will have a flag we can look at like `isVirtualProductRelated`
  const aIsVirtual = getIsVirtual(a)
  const bIsVirtual = getIsVirtual(b)
  if ((aIsVirtual || bIsVirtual) && aIsVirtual !== bIsVirtual) {
    return aIsVirtual ? -1 : 1
  }

  const aRecommended = getIsRecommended(a)
  const bRecommended = getIsRecommended(b)
  if ((aRecommended || bRecommended) && aRecommended !== bRecommended) {
    return aRecommended ? -1 : 1
  }

  const firstAmount = Number(a.isStaticOffer ? a.amount : getApprovalMaxAmount(a, aOption));
  const secondAmount = Number(b.isStaticOffer ? b.amount : getApprovalMaxAmount(b, bOption));

  if (firstAmount === secondAmount || (!Number.isFinite(firstAmount) && !Number.isFinite(secondAmount))) {
    const firstLoanTypePriority = TYPE_ORDER.indexOf(normalizeLoanType(a?.loanProductCategoryType)) + 1;
    const secondLoanTypePriority = TYPE_ORDER.indexOf(normalizeLoanType(b?.loanProductCategoryType)) + 1;

    if (firstLoanTypePriority !== secondLoanTypePriority) {
      return secondLoanTypePriority - firstLoanTypePriority
    }

    // sort by prioritized lender
    const aPrioritized = getIsPrioritized(a)
    const bPrioritized = getIsPrioritized(b)

    if ((aPrioritized || bPrioritized) && aPrioritized !== bPrioritized) {
      return aPrioritized ? -1 : 1
    }

    if (!Number.isFinite(firstAmount) && !Number.isFinite(secondAmount)) {
      return new Date(compatibleDate(a.created)) - new Date(compatibleDate(b.created))
    }

    const firstPayback = !a.isStaticOffer && a.options.length
      ? output(a, { optionId: aOption.id })?.payback
      : getOfferPayback(a)

    const secondPayback = !b.isStaticOffer && b.options.length
      ? output(b, { optionId: bOption.id })?.payback
      : getOfferPayback(b)

    if (!firstPayback && !secondPayback) {
      return new Date(compatibleDate(a.created)) - new Date(compatibleDate(b.created))
    }

    if (!firstPayback) {
      return 1
    } else if (!secondPayback) {
      return -1
    }

    const firstCostOfCapital = firstPayback - firstAmount
    const secondCostOfCapital = secondPayback - secondAmount

    return firstCostOfCapital - secondCostOfCapital
  }

  if (!Number.isFinite(firstAmount)) {
    return 1
  } else if (!Number.isFinite(secondAmount)) {
    return -1
  }

  // sort from max to min amount
  return secondAmount - firstAmount
}

export function sortOptionsByParam (param, output, initialChange, a, b) {
  const splitParam = param.split('-')
  const direction = splitParam.pop() === 'asc' ? 'asc' : 'desc'
  const sortBy = splitParam.join('-')

  switch (sortBy) {
    case 'amount':
      return sortByAmount(a, b, direction, output, initialChange)
    case 'cost':
      return sortByCost(a, b, direction, output, initialChange)
    case 'term':
      return sortByTerm(a, b, direction, output, initialChange)
    default:
      if (direction === 'asc') {
        return new Date(compatibleDate(a.created)) - new Date(compatibleDate(b.created))
      } else {
        return new Date(compatibleDate(b.created)) - new Date(compatibleDate(a.created))
      }
  }
}

function sortByAmount(a, b, direction, output, initialChange) {
  const aOption = !a.isStaticOffer ? getApprovalOption(a, initialChange) : {};
  const bOption = !b.isStaticOffer ? getApprovalOption(b, initialChange) : {};

  const firstAmount = Number(a.isStaticOffer ? a.amount : getApprovalMaxAmount(a, aOption));
  const secondAmount = Number(b.isStaticOffer ? b.amount : getApprovalMaxAmount(b, bOption));

  if (firstAmount === secondAmount || (!Number.isFinite(firstAmount) && !Number.isFinite(secondAmount))) {
    // Position first recommended option
    const aRecommended = getIsRecommended(a)
    const bRecommended = getIsRecommended(b)
    if ((aRecommended || bRecommended) && aRecommended !== bRecommended) {
      if (direction === 'asc') {
        return aRecommended ? 1 : -1
      } else {
        return aRecommended ? -1 : 1
      }
    }

    const firstLoanTypePriority = TYPE_ORDER.indexOf(normalizeLoanType(a?.loanProductCategoryType)) + 1;
    const secondLoanTypePriority = TYPE_ORDER.indexOf(normalizeLoanType(b?.loanProductCategoryType)) + 1;

    if (firstLoanTypePriority !== secondLoanTypePriority) {
      if (direction === 'asc') {
        return firstLoanTypePriority - secondLoanTypePriority
      } else {
        return secondLoanTypePriority - firstLoanTypePriority
      }
    }

    // sort by prioritized lender
    const aPrioritized = getIsPrioritized(a)
    const bPrioritized = getIsPrioritized(b)

    if ((aPrioritized || bPrioritized) && aPrioritized !== bPrioritized) {
      if (direction === 'asc') {
        return aPrioritized ? 1 : -1
      } else {
        return aPrioritized ? -1 : 1
      }
    }

    if (direction === 'asc') {
      return new Date(compatibleDate(a.created)) - new Date(compatibleDate(b.created))
    } else {
      return new Date(compatibleDate(b.created)) - new Date(compatibleDate(a.created))
    }
  }

  if (!Number.isFinite(firstAmount)) {
    return direction === 'asc' ? -1 : 1
  } else if (!Number.isFinite(secondAmount)) {
    return direction === 'asc' ? 1 : -1
  }


  if (direction === 'asc') {
    return firstAmount - secondAmount
  } else {
    return secondAmount - firstAmount
  }
}

function sortByCost(a, b, direction, output, initialChange) {
  const aOption = !a.isStaticOffer ? getApprovalOption(a, initialChange) : {};
  const bOption = !b.isStaticOffer ? getApprovalOption(b, initialChange) : {};

  const firstPayback = !a.isStaticOffer && a.options.length
    ? output(a, { optionId: aOption.id })?.payback
    : getOfferPayback(a)

  const secondPayback = !b.isStaticOffer && b.options.length
    ? output(b, { optionId: bOption.id })?.payback
    : getOfferPayback(b)

  const firstAmount = Number(a.isStaticOffer ? a.amount : getApprovalMaxAmount(a, aOption));
  const secondAmount = Number(b.isStaticOffer ? b.amount : getApprovalMaxAmount(b, bOption));

  const firstCostOfCapital = (firstPayback || 0) - firstAmount
  const secondCostOfCapital = (secondPayback || 0) - secondAmount

  if ((!firstPayback && !secondPayback) || (!Number.isFinite(firstAmount) && !Number.isFinite(secondAmount)) || firstCostOfCapital === secondCostOfCapital) {
    // Position first recommended option
    const aRecommended = getIsRecommended(a)
    const bRecommended = getIsRecommended(b)
    if ((aRecommended || bRecommended) && aRecommended !== bRecommended) {
      if (direction === 'asc') {
        return aRecommended ? 1 : -1
      } else {
        return aRecommended ? -1 : 1
      }
    }

    if (Number.isFinite(firstAmount) && Number.isFinite(secondAmount) && firstAmount !== secondAmount) {
      return direction === 'asc' ? firstAmount - secondAmount : secondAmount - firstAmount
    }
    if (Number.isFinite(firstAmount) && !Number.isFinite(secondAmount)) {
      return direction === 'asc' ? -1 : 1
    }
    if (Number.isFinite(secondAmount) && !Number.isFinite(firstAmount)) {
      return direction === 'asc' ? 1 : -1
    }

    const firstLoanTypePriority = TYPE_ORDER.indexOf(normalizeLoanType(a?.loanProductCategoryType)) + 1;
    const secondLoanTypePriority = TYPE_ORDER.indexOf(normalizeLoanType(b?.loanProductCategoryType)) + 1;

    if (firstLoanTypePriority !== secondLoanTypePriority) {
      if (direction === 'asc') {
        return firstLoanTypePriority - secondLoanTypePriority
      } else {
        return secondLoanTypePriority - firstLoanTypePriority
      }
    }

    // sort by prioritized lender
    const aPrioritized = getIsPrioritized(a)
    const bPrioritized = getIsPrioritized(b)

    if ((aPrioritized || bPrioritized) && aPrioritized !== bPrioritized) {
      if (direction === 'asc') {
        return aPrioritized ? 1 : -1
      } else {
        return aPrioritized ? -1 : 1
      }
    }

    if (direction === 'asc') {
      return new Date(compatibleDate(a.created)) - new Date(compatibleDate(b.created))
    } else {
      return new Date(compatibleDate(b.created)) - new Date(compatibleDate(a.created))
    }
  }

  if (!Number.isFinite(firstAmount) || !firstPayback) {
    return direction === 'asc' ? -1 : 1
  } else if (!Number.isFinite(secondAmount) || !secondPayback) {
    return direction === 'asc' ? 1 : -1
  }

  if (direction === 'asc') {
    return firstCostOfCapital - secondCostOfCapital
  } else {
    return secondCostOfCapital - firstCostOfCapital
  }
}

function sortByTerm(a, b, direction, output, initialChange) {
  const aOption = !a.isStaticOffer ? getApprovalOption(a, initialChange) : {};
  const bOption = !b.isStaticOffer ? getApprovalOption(b, initialChange) : {};

  const firstTerm = !a.isStaticOffer && a.options.length
    ? output(a, { optionId: aOption.id })?.term
    : a.term

  const secondTerm = !b.isStaticOffer && b.options.length
    ? output(b, { optionId: bOption.id })?.term
    : b.term

  if (firstTerm === secondTerm || (!firstTerm && !secondTerm)) {
    // Position first recommended option
    const aRecommended = getIsRecommended(a)
    const bRecommended = getIsRecommended(b)
    if ((aRecommended || bRecommended) && aRecommended !== bRecommended) {
      if (direction === 'asc') {
        return aRecommended ? 1 : -1
      } else {
        return aRecommended ? -1 : 1
      }
    }

    const firstLoanTypePriority = TYPE_ORDER.indexOf(normalizeLoanType(a?.loanProductCategoryType)) + 1;
    const secondLoanTypePriority = TYPE_ORDER.indexOf(normalizeLoanType(b?.loanProductCategoryType)) + 1;

    if (firstLoanTypePriority !== secondLoanTypePriority) {
      if (direction === 'asc') {
        return firstLoanTypePriority - secondLoanTypePriority
      } else {
        return secondLoanTypePriority - firstLoanTypePriority
      }
    }

    // sort by prioritized lender
    const aPrioritized = getIsPrioritized(a)
    const bPrioritized = getIsPrioritized(b)

    if ((aPrioritized || bPrioritized) && aPrioritized !== bPrioritized) {
      if (direction === 'asc') {
        return aPrioritized ? 1 : -1
      } else {
        return aPrioritized ? -1 : 1
      }
    }

    if (direction === 'asc') {
      return new Date(compatibleDate(a.created)) - new Date(compatibleDate(b.created))
    } else {
      return new Date(compatibleDate(b.created)) - new Date(compatibleDate(a.created))
    }
  }

  if (!firstTerm) {
    return direction === 'asc' ? -1 : 1
  } else if (!secondTerm) {
    return direction === 'asc' ? 1 : -1
  }

  if (direction === 'asc') {
    return Number(firstTerm) - Number(secondTerm)
  } else {
    return Number(secondTerm) - Number(firstTerm)
  }
}
